import React, { useEffect, useRef, useState } from "react";
import { api } from "../../lib/api";
import { T, T_GLOBAL, datosDelError } from "../../lib/cfdi";
import { formatoDinero } from "../../lib/money";
import { etiquetaForma } from "../../lib/anticipos";
import { useAuth } from "../../contexts/AuthContext";

// Fecha de liquidación en hora de México: la global se arma por mes local, no UTC.
const fechaOp = (iso) => (iso ? new Date(iso).toLocaleDateString("es-MX", { timeZone: "America/Mexico_City", day: "2-digit", month: "2-digit", year: "numeric" }) : "—");

const btn = "rounded-lg border border-slate-200 px-3 py-2 text-sm disabled:opacity-50";

export function DetalleGlobal({ cfdi, onCambio }) {
  const { puede } = useAuth();
  const [actual, setActual] = useState(cfdi);
  const [confirmar, setConfirmar] = useState(null);
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState(null);
  const [incierto, setIncierto] = useState(false);
  useEffect(() => { setActual(cfdi); setIncierto(false); }, [cfdi]);
  const accion = async (op) => {
    setOcupado(true); setError(null);
    try {
      const { data } = await api.post(`/cfdis/${encodeURIComponent(actual.id)}/${op}`);
      setActual(data); setConfirmar(null); setIncierto(false);
      await onCambio?.();
    } catch (e) {
      setError(datosDelError(e).detail); setIncierto(true);
    } finally { setOcupado(false); }
  };
  return (
    <div className="mt-3 space-y-2" data-testid="global-detalle">
      <p>{T_GLOBAL.titulo} · {actual.global_periodo} · {T_GLOBAL.operaciones(actual.numero_operaciones ?? actual.operaciones?.length ?? 0)}</p>
      <p>{T.estados[actual.estado]}</p>
      {actual.global_reemplazo_id && <p data-testid="global-reexpedicion">{T_GLOBAL.reexpedicion}: {T.estados[actual.global_reemplazo_estado] || actual.global_reemplazo_id}</p>}
      <button className={btn} disabled={ocupado} onClick={() => accion("reconciliar")} data-testid="global-actualizar">{T.actualizar}</button>
      {actual.estado === "vigente" && !incierto && puede?.("cfdi_cancelar") && (actual.operaciones || []).map((o) => (
        <div key={o.id}>
          <span>{fechaOp(o.liquidado_at)} · {formatoDinero(o.total)} </span>
          <button className={btn} disabled={ocupado} onClick={() => setConfirmar(o.id)} data-testid={`global-quitar-${o.id}`}>{T_GLOBAL.quitar}</button>
        </div>
      ))}
      {confirmar && <div>
        <p>{T_GLOBAL.confirmar}</p>
        <button className={btn} disabled={ocupado || incierto} onClick={() => accion(`excluir/${encodeURIComponent(confirmar)}`)} data-testid="global-confirmar">{T_GLOBAL.quitar}</button>
        <button className={btn} disabled={ocupado} onClick={() => setConfirmar(null)}>{T.volver}</button>
      </div>}
      {error && <p role="alert">{error}</p>}
    </div>
  );
}

export default function FacturaGlobal({ emisorId, ultimoMes, onCambio }) {
  const [mes, setMes] = useState(ultimoMes || "");
  const [previa, setPrevia] = useState(null);
  const [actual, setActual] = useState(null);
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState(null);
  const version = useRef(0);
  useEffect(() => () => { version.current += 1; }, []);
  const validarMes = Boolean(mes && ultimoMes && mes <= ultimoMes);
  const consultar = async () => {
    const n = ++version.current;
    setOcupado(true); setError(null); setPrevia(null); setActual(null);
    try {
      const { data } = await api.get("/cfdi-pacientes/global/vista-previa", { params: { emisor_id: emisorId, periodo: mes } });
      if (n === version.current) { setPrevia(data); setActual(data.global_actual); }
    } catch (e) { if (n === version.current) setError(datosDelError(e).detail); }
    finally { if (n === version.current) setOcupado(false); }
  };
  const emitir = async () => {
    setOcupado(true); setError(null); setPrevia(null);
    try {
      const { data } = await api.post("/cfdi-pacientes/global", { emisor_id: emisorId, periodo: mes });
      setActual(data); await onCambio?.();
    } catch (e) {
      const d = datosDelError(e); setError(d.detail);
      if (d.extra.cfdi_id) setActual({ id: d.extra.cfdi_id, global_periodo: mes, estado: "reservado", operaciones: [] });
    } finally { setOcupado(false); }
  };
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="factura-global">
      <h3>{T_GLOBAL.titulo}</h3>
      <label>{T_GLOBAL.mes}
        <input type="month" value={mes} max={ultimoMes} disabled={ocupado} data-testid="global-mes"
               onChange={(e) => { version.current += 1; setMes(e.target.value); setPrevia(null); setActual(null); setError(null); }} />
      </label>
      <button className={btn} disabled={ocupado || !validarMes} onClick={consultar} data-testid="global-previa">{T.verVistaPrevia}</button>
      {previa && <div data-testid="global-previa-datos">
        {previa.modo === "test" && <p>{T.modoPrueba}</p>}
        <p>{T_GLOBAL.operaciones(previa.operaciones.length)} · {T.total}: {formatoDinero(previa.total)}</p>
        <p>{T.formaPago}: {etiquetaForma(previa.forma_pago)}</p>
        {Object.entries(previa.totales_por_tasa).map(([t, m]) => <p key={t}>{T.iva} {t}: {formatoDinero(m)}</p>)}
        <ul>{previa.operaciones.map((o) => <li key={o.id}>{fechaOp(o.liquidado_at)} · {formatoDinero(o.total)}</li>)}</ul>
        {!actual && <button className={btn} disabled={ocupado || !previa.operaciones.length} onClick={emitir} data-testid="global-emitir">{T.emitir}</button>}
      </div>}
      {actual && <DetalleGlobal cfdi={actual} onCambio={onCambio} />}
      {error && <p role="alert">{error}</p>}
    </section>
  );
}
